import * as React from 'react'

import type { DashboardKpis, SalesIndicator } from '@sdr-agend/admin-schemas'
import { formatCurrencyBRL, formatPercentOrDash } from '@/lib/format'
import { noShowRate, ticketMedio } from '@/lib/dashboard-metrics'

/**
 * "Reuniões e Resultado Comercial" (Story 10.7, 2026-07-30, user request,
 * AC4) — consolida em um único painel dedicado números que hoje já existem
 * mas ficam espalhados em cards separados: agendadas/realizadas/no-shows/
 * comparecimento (de `DashboardKpis`) + vendas/faturamento (de
 * `SalesIndicator`) + ticket médio.
 */
export interface CommercialResultsPanelProps {
  kpis: DashboardKpis
  salesIndicator: SalesIndicator
}

interface Row {
  key: string
  label: string
  value: string
}

export function CommercialResultsPanel({
  kpis,
  salesIndicator,
}: CommercialResultsPanelProps): React.ReactElement {
  const ticket = ticketMedio(salesIndicator)

  const rows: Row[] = [
    { key: 'scheduled', label: 'Agendadas', value: kpis.meetingsScheduled.toLocaleString('pt-BR') },
    { key: 'attended', label: 'Realizadas', value: kpis.meetingsAttended.toLocaleString('pt-BR') },
    { key: 'noShow', label: 'No-shows', value: kpis.meetingsNoShow.toLocaleString('pt-BR') },
    { key: 'attendanceRate', label: 'Comparecimento', value: formatPercentOrDash(kpis.attendanceRate) },
    {
      key: 'noShowRate',
      label: 'Taxa de No-show',
      value: formatPercentOrDash(noShowRate(kpis.attendanceRate)),
    },
    { key: 'sales', label: 'Vendas (total GHL)', value: salesIndicator.totalCount.toLocaleString('pt-BR') },
    {
      key: 'revenue',
      label: 'Faturamento (total GHL)',
      value: formatCurrencyBRL(salesIndicator.totalValueSum),
    },
    {
      key: 'salesAttributed',
      label: 'Vendas atribuídas à IA',
      value: salesIndicator.count.toLocaleString('pt-BR'),
    },
    {
      key: 'ticket',
      label: 'Ticket médio',
      value: ticket === null ? 'N/A' : formatCurrencyBRL(ticket),
    },
  ]

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="commercial-results-panel">
      {rows.map((row) => (
        <div key={row.key} className="rounded-md border p-2.5" data-testid={`commercial-results-${row.key}`}>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{row.label}</p>
          <p className="text-lg font-semibold tabular-nums">{row.value}</p>
        </div>
      ))}
    </div>
  )
}

export default CommercialResultsPanel
